// ============================================================
// BLOQUE 32 — RFW-READDY-NOTIFICATIONS-001
// Servicio de preferencias de notificación.
// El usuario controla qué tipos de notificaciones recibe.
// Ninguna radio puede activar preferencias sin consentimiento.
// ============================================================

import { getSupabaseClient } from '@/services/supabase/client';
import type {
  NotificationPreference,
  PreferenceType,
  UpdatePreferencePayload,
  PreferencesMap,
} from '@/types/notifications';

// ─── Lectura ────────────────────────────────────────────────

/**
 * Obtiene todas las preferencias de notificación del usuario autenticado.
 * RLS garantiza que solo ve sus propias preferencias.
 */
export async function getMyPreferences(): Promise<NotificationPreference[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .order('preference_type', { ascending: true });

  if (error) throw new Error(`Error al obtener preferencias: ${error.message}`);

  return (data || []) as NotificationPreference[];
}

/**
 * Obtiene las preferencias agrupadas por tipo.
 */
export async function getMyPreferencesGrouped(): Promise<PreferencesMap> {
  const prefs = await getMyPreferences();

  const map: PreferencesMap = {} as PreferencesMap;

  for (const pref of prefs) {
    if (!map[pref.preference_type]) {
      map[pref.preference_type] = [];
    }
    map[pref.preference_type].push(pref);
  }

  return map;
}

/**
 * Verifica si un tipo de notificación está habilitado para el usuario.
 */
export async function isPreferenceEnabled(
  preferenceType: PreferenceType,
  referenceId?: string | null,
): Promise<boolean> {
  const supabase = getSupabaseClient();

  let query = supabase
    .from('notification_preferences')
    .select('enabled')
    .eq('preference_type', preferenceType);

  if (referenceId) {
    query = query.eq('reference_id', referenceId);
  } else {
    query = query.is('reference_id', null);
  }

  const { data, error } = await query.maybeSingle();

  if (error || !data) {
    // Si no existe preferencia explícita, por defecto está habilitado
    return true;
  }

  return data.enabled;
}

/**
 * Obtiene la preferencia global de notificaciones del usuario.
 * Si está desactivada, todas las notificaciones se silencian.
 */
export async function getGlobalPreference(): Promise<boolean> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('enabled')
    .eq('preference_type', 'global')
    .is('reference_id', null)
    .maybeSingle();

  if (error || !data) {
    return true; // Por defecto, habilitado
  }

  return data.enabled;
}

// ─── Escritura ──────────────────────────────────────────────

/**
 * Crea o actualiza una preferencia individual.
 * Si la preferencia no existe, se crea; si existe, se actualiza.
 * Upsert protegido por RLS: solo el usuario puede modificar sus preferencias.
 */
export async function upsertPreference(
  payload: UpdatePreferencePayload,
): Promise<NotificationPreference> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('notification_preferences')
    .upsert(
      {
        preference_type: payload.preference_type,
        reference_id: payload.reference_id || null,
        enabled: payload.enabled,
      },
      {
        onConflict: 'profile_id,preference_type,reference_id',
        ignoreDuplicates: false,
      },
    )
    .select()
    .single();

  if (error) throw new Error(`Error al guardar preferencia: ${error.message}`);

  return data as NotificationPreference;
}

/**
 * Activa una preferencia específica.
 */
export async function enablePreference(
  preferenceType: PreferenceType,
  referenceId?: string | null,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: preferenceType,
    reference_id: referenceId || null,
    enabled: true,
  });
}

/**
 * Desactiva una preferencia específica.
 */
export async function disablePreference(
  preferenceType: PreferenceType,
  referenceId?: string | null,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: preferenceType,
    reference_id: referenceId || null,
    enabled: false,
  });
}

/**
 * Activa o desactiva las notificaciones globales del usuario.
 * Cuando global está desactivado, ninguna notificación se entrega.
 */
export async function setGlobalPreference(enabled: boolean): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'global',
    reference_id: null,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones para una radio específica.
 * Requiere consentimiento explícito del usuario: solo el propio usuario
 * puede llamar esta función (RLS). Una radio NO puede auto-activarse.
 */
export async function setRadioNotificationPreference(
  radioId: string,
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'radio',
    reference_id: radioId,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones para un programa específico.
 */
export async function setProgramNotificationPreference(
  programId: string,
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'program',
    reference_id: programId,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones para un locutor específico.
 */
export async function setAnnouncerNotificationPreference(
  announcerId: string,
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'announcer',
    reference_id: announcerId,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones para un podcast específico.
 */
export async function setPodcastNotificationPreference(
  podcastId: string,
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'podcast',
    reference_id: podcastId,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones de concursos.
 */
export async function setContestNotificationPreference(
  enabled: boolean,
  contestId?: string | null,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'contest',
    reference_id: contestId || null,
    enabled,
  });
}

/**
 * Activa o desactiva notificaciones de solicitudes (song requests, greetings).
 */
export async function setRequestNotificationPreference(
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'request',
    reference_id: null,
    enabled,
  });
}

/**
 * Activa o desactiva avisos administrativos / del sistema.
 */
export async function setAdminNoticePreference(
  enabled: boolean,
): Promise<NotificationPreference> {
  return upsertPreference({
    preference_type: 'admin_notice',
    reference_id: null,
    enabled,
  });
}

/**
 * Reinicia todas las preferencias a los valores por defecto.
 * Borra todas las preferencias explícitas del usuario.
 * Los defaults (todo habilitado) se aplican cuando no hay registro.
 */
export async function resetAllPreferences(): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from('notification_preferences')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000'); // delete all

  if (error) throw new Error(`Error al resetear preferencias: ${error.message}`);
}

/**
 * Activa en lote las preferencias por defecto para las notificaciones
 * de una radio que el usuario acaba de seguir.
 *
 * IMPORTANTE: Solo el usuario puede llamar esta función (RLS).
 * La radio NO puede auto-activar preferencias sin consentimiento.
 */
export async function initializeRadioPreferences(radioId: string): Promise<void> {
  const defaults: UpdatePreferencePayload[] = [
    { preference_type: 'radio', reference_id: radioId, enabled: true },
    { preference_type: 'program', reference_id: radioId, enabled: true },
    { preference_type: 'announcer', reference_id: radioId, enabled: true },
    { preference_type: 'podcast', reference_id: radioId, enabled: true },
    { preference_type: 'contest', reference_id: radioId, enabled: true },
  ];

  const supabase = getSupabaseClient();

  const rows = defaults.map((d) => ({
    preference_type: d.preference_type,
    reference_id: d.reference_id,
    enabled: d.enabled,
  }));

  const { error } = await supabase
    .from('notification_preferences')
    .upsert(rows, {
      onConflict: 'profile_id,preference_type,reference_id',
      ignoreDuplicates: true, // No sobreescribir si el usuario ya configuró
    });

  if (error) throw new Error(`Error al inicializar preferencias: ${error.message}`);
}

// ============================================================
// CONTRATO — LO QUE EL FRONTEND NUNCA DEBE HACER
// ============================================================
//
// ❌ Activar preferencias para otro usuario
// ❌ Permitir que una radio active preferencias sin consentimiento
// ❌ Modificar preferencias sin autenticación
//
// El usuario siempre tiene control total sobre sus preferencias.